import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { applications } from '../mock';

export const Applications = () => {
  const { t } = useTranslation();

  // Get translated application data
  const getApplicationData = (application) => {
    const appData = t(`applications.items.${application.id}`, { returnObjects: true });
    return {
      ...application,
      title: appData?.title || '',
      description: appData?.description || '',
      features: appData?.features || []
    };
  };

  const translatedApplications = applications.map(getApplicationData);
  
  return (
    <div className="flex flex-col">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-neutral-900 via-neutral-800 to-emerald-900 text-white py-12 sm:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-emerald-400 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-4">{t('applications.subtitle')}</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 lg:mb-6">{t('applications.title')}</h1>
          <p className="text-base sm:text-lg lg:text-xl text-neutral-300 max-w-3xl leading-relaxed">
            {t('applications.description')}
          </p>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-10 sm:py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 sm:space-y-12 lg:space-y-16">
            {translatedApplications.map((application, index) => (
              <div 
                key={application.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="aspect-video bg-neutral-100 rounded-lg overflow-hidden">
                    <img 
                      src={application.image} 
                      alt={application.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4">{application.title}</h2>
                  <p className="text-neutral-600 text-sm sm:text-base lg:text-lg mb-4 sm:mb-6">{application.description}</p>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    {application.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-emerald-600 mr-2 sm:mr-3 flex-shrink-0"></div>
                        <span className="text-xs sm:text-sm font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-10 sm:py-12 lg:py-16 bg-neutral-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center mb-6 sm:mb-8 lg:mb-12">{t('applications.industries')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-sm sm:text-base lg:text-lg">{t('applications.municipalities')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs sm:text-sm text-neutral-600">{t('applications.municipalitiesDesc')}</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-sm sm:text-base lg:text-lg">{t('applications.wasteManagement')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs sm:text-sm text-neutral-600">{t('applications.wasteManagementDesc')}</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-sm sm:text-base lg:text-lg">{t('applications.industrialFacilities')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs sm:text-sm text-neutral-600">{t('applications.industrialFacilitiesDesc')}</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-sm sm:text-base lg:text-lg">{t('applications.commercial')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs sm:text-sm text-neutral-600">{t('applications.commercialDesc')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Applications;
